import { useState } from 'react';

import useFetch from 'hooks/useFetch';
import useGlobalStore from 'hooks/useGlobalStore';
import useRetry from 'hooks/useRetry';

import useTranslation from './hooks/useTranslation';

import { ORGANIZATIONS_API } from './constants';
import type { CreateOrganization, FetchOrganizations } from './types';

const useOrganizations = () => {
    const [isLoading, setIsLoading] = useState(false);
    const { withRetry } = useRetry();
    const { CONNECTION_ERROR, SAVE_FAILED } = useTranslation();
    const { authenticatedGet, authenticatedPost } = useFetch();

    const { setValue } = useGlobalStore({
        namespace: 'useOrganizations'
    });

    const createOrganization: CreateOrganization = async values => {
        try {
            setIsLoading(true);

            return await withRetry(
                () => authenticatedPost(ORGANIZATIONS_API, JSON.stringify(values)),
                SAVE_FAILED,
                () => createOrganization(values)
            );
        } finally {
            setIsLoading(false);
        }
    };

    const fetchOrganizations: FetchOrganizations = async () => {
        try {
            setIsLoading(true);

            const data = await withRetry(
                () => authenticatedGet(ORGANIZATIONS_API),
                CONNECTION_ERROR,
                () => fetchOrganizations()
            );

            if (data) {
                setValue({ organizations: data });
            }

            return data;
        } finally {
            setIsLoading(false);
        }
    };

    return {
        createOrganization,
        fetchOrganizations,
        isLoading
    };
};

export default useOrganizations;
